import { useState, useCallback, useEffect } from "react";
import { X, Search, ChevronLeft, ArrowLeftRight } from "lucide-react";
import type { ClinicStockItem } from "../../services/api/admin-order.types";
import { AdminOrderManagementService } from "../../services/api/admin-order.service";

interface ReplacementModalProps {
  onClose: () => void;
  onConfirm: (replacement: {
    substituteProductId: string;
    substituteInwardId: string;
    substituteName: string;
    substituteImage: string;
    substituteManufacturer: string;
    substitutePrice: number;
    substituteMrp: number;
    substituteNote: string;
  }) => void;
  clinicId: string;
  originalProduct: { name: string; image: string; manufacturer: string; quantity: number };
  initialResults?: ClinicStockItem[];
}

const mapRawToClinicStockItem = (raw: any): ClinicStockItem => {
  const mrpVal = parseFloat(raw.mrp) || 0;
  const tabletPerStripe = raw.product?.tablet_per_stripe || 1;
  return {
    id: raw.id as any,
    inwardId: raw.id,
    productId: raw.product_id || raw.product?.id || "",
    productName: raw.product?.name || "",
    productImage: raw.product?.productImage || "",
    manufacturer: raw.manufacturer?.manufacturer_name || "",
    batchNo: raw.batch_number || "",
    expiryDate: raw.expiry_date || "",
    mrp: mrpVal,
    mrpPerUnit: parseFloat((mrpVal / tabletPerStripe).toFixed(2)),
    sellingPrice: parseFloat(raw.finalSP) || parseFloat(raw.landing_cost) || 0,
    strips: raw.remaining_stocks || 0,
    tablets: raw.totalQuantity || ((raw.remaining_stocks || 0) * tabletPerStripe),
    landingCost: parseFloat(raw.landing_cost) || 0,
  };
};

const ReplacementModal: React.FC<ReplacementModalProps> = ({ onClose, onConfirm, clinicId, originalProduct, initialResults }) => {
  const [step, setStep] = useState<"search" | "confirm">("search");
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<ClinicStockItem[]>(() => {
    return initialResults ? initialResults.map(mapRawToClinicStockItem) : [];
  });
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ClinicStockItem | null>(null);
  const [note, setNote] = useState("");
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (initialResults && initialResults.length > 0) {
      setResults(initialResults.map(mapRawToClinicStockItem));
    } else {
      // Trigger initial fetch on mount so list is not blank initially
      const fetchInitial = async () => {
        try {
          setLoading(true);
          const response = await AdminOrderManagementService.getClinicStock(clinicId, 1, 15, "");
          if (response.success && response.data) {
            const data = response.data as { stocks?: any[]; data?: any[] };
            const rawItems = data.stocks || data.data || [];
            setResults(rawItems.map(mapRawToClinicStockItem));
          }
        } catch (error) {
          console.error("Clinic stock initial fetch error:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchInitial();
    }
  }, [clinicId, initialResults]);

  const handleSearch = useCallback(
    (text: string) => {
      setSearch(text);
      if (debounceTimer) clearTimeout(debounceTimer);
      const timer = setTimeout(async () => {
        try {
          setLoading(true);
          const response = await AdminOrderManagementService.getClinicStock(clinicId, 1, 15, text);
          if (response.success && response.data) {
            const data = response.data as { stocks?: any[]; data?: any[] };
            const rawItems = data.stocks || data.data || [];
            setResults(rawItems.map(mapRawToClinicStockItem));
          }
        } catch (error) {
          console.error("Clinic stock error:", error);
        } finally {
          setLoading(false);
        }
      }, 400);
      setDebounceTimer(timer);
    },
    [debounceTimer, clinicId]
  );

  const selectItem = (item: ClinicStockItem) => {
    setSelectedItem(item);
    setStep("confirm");
  };

  const handleConfirm = () => {
    if (!selectedItem) return;
    onConfirm({
      substituteProductId: selectedItem.productId,
      substituteInwardId: selectedItem.inwardId,
      substituteName: selectedItem.productName,
      substituteImage: selectedItem.productImage,
      substituteManufacturer: selectedItem.manufacturer,
      substitutePrice: selectedItem.sellingPrice || selectedItem.landingCost,
      substituteMrp: selectedItem.mrp,
      substituteNote: note,
    });
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[1000] flex items-center justify-center animate-fadeIn" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-[700px] w-[95%] max-h-[85vh] flex flex-col shadow-2xl animate-slideUp" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-sm font-bold text-slate-800">Replacement Medicines</h3>
          <button className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors border-none cursor-pointer" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {step === "search" ? (
            <>
              {/* Search */}
              <div className="relative mb-4">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by name, batch, manufacturer..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                />
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[550px]">
                  <thead>
                    <tr className="border-b border-slate-200">
                      {["Name", "MRP", "MRP/Unit", "Strips", "Tablets", "Action"].map((h) => (
                        <th key={h} className="px-3 py-2 text-left font-semibold text-slate-500 text-[0.65rem] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={6} className="py-8 text-center text-slate-400">Loading...</td></tr>
                    ) : results.length === 0 ? (
                      <tr><td colSpan={6} className="py-8 text-center text-slate-400">Search for products</td></tr>
                    ) : (
                      results.map((item, i) => (
                        <tr key={`${item.productId}-${i}`} className="border-b border-slate-50 hover:bg-slate-50/80 transition-colors">
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-2">
                              <img src={item.productImage} alt="" className="w-8 h-8 rounded-md object-cover bg-slate-100 border border-slate-100"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                              <div>
                                <div className="font-semibold text-slate-800">{item.productName}</div>
                                <div className="text-[0.6rem] text-slate-400">{item.manufacturer}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 font-medium">₹ {item.mrp}</td>
                          <td className="px-3 py-2.5">₹ {item.mrpPerUnit}</td>
                          <td className="px-3 py-2.5">{item.strips}</td>
                          <td className="px-3 py-2.5">{item.tablets}</td>
                          <td className="px-3 py-2.5">
                            <button
                              className="px-3 py-1.5 rounded-lg text-[0.7rem] font-semibold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors border-none cursor-pointer"
                              onClick={() => selectItem(item)}
                            >
                              Select
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            /* Confirm step */
            <div>
              <button
                className="flex items-center gap-1 text-xs text-slate-500 font-semibold hover:text-slate-700 bg-transparent border-none cursor-pointer mb-4"
                onClick={() => setStep("search")}
              >
                <ChevronLeft size={14} /> Selected Replacement Medicines
              </button>

              {/* Side-by-side comparison */}
              <div className="flex items-center gap-4 mb-5">
                {/* Current */}
                <div className="flex-1 p-4 rounded-xl border border-slate-200 bg-slate-50">
                  <div className="text-[0.65rem] font-semibold text-slate-400 mb-2">Current Medicine:</div>
                  <div className="flex items-center gap-2">
                    <img src={originalProduct.image} alt="" className="w-10 h-10 rounded-lg object-cover bg-white border border-slate-200"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                    <div>
                      <div className="text-xs font-bold text-slate-800">{originalProduct.name}</div>
                      <div className="text-[0.6rem] text-slate-400">{originalProduct.manufacturer}</div>
                    </div>
                    <span className="text-xs font-bold text-red-500 ml-auto">-{originalProduct.quantity} qty</span>
                  </div>
                </div>

                {/* Swap icon */}
                <div className="flex-shrink-0">
                  <ArrowLeftRight size={18} className="text-slate-400" />
                </div>

                {/* Replacement */}
                <div className="flex-1 p-4 rounded-xl border border-slate-200 bg-slate-50">
                  <div className="text-[0.65rem] font-semibold text-slate-400 mb-2">Replace With:</div>
                  {selectedItem && (
                    <div className="flex items-center gap-2">
                      <img src={selectedItem.productImage} alt="" className="w-10 h-10 rounded-lg object-cover bg-white border border-slate-200"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                      <div>
                        <div className="text-xs font-bold text-slate-800">{selectedItem.productName}</div>
                        <div className="text-[0.6rem] text-slate-400">{selectedItem.manufacturer}</div>
                      </div>
                      <span className="text-xs font-bold text-emerald-500 ml-auto">+{originalProduct.quantity} qty</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Note */}
              <div className="mb-5">
                <label className="text-xs font-medium text-slate-600 block mb-1.5">Note for Replacement (optional)</label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Note"
                  className="w-full px-3 py-2.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none h-24 transition-all"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold border border-emerald-300 text-emerald-600 bg-white hover:bg-emerald-50 transition-colors cursor-pointer"
                  onClick={() => setStep("search")}
                >
                  <ChevronLeft size={12} /> Back to Select
                </button>
                <button
                  className="px-4 py-2 rounded-lg text-xs font-semibold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors border-none cursor-pointer"
                  onClick={handleConfirm}
                >
                  Confirm
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReplacementModal;
